import { Coins, X } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Inline confirmation modal — caller controls open state.
// Shows "this will cost N credits" and a confirm/cancel pair.
// Used by Scorecard, ScorerChat, etc. before invoking a credit-debiting endpoint.
//
// 2026-06-08: dismissal switched to useDialogDismiss, which adds focus
// trap + hardware-back close + body scroll-lock on top of the Esc /
// backdrop handlers we already had. Esc / backdrop still no-ops while
// busy so the user can't cancel mid-debit.
export default function CreditConfirmModal({
  open,
  onConfirm,
  onCancel,
  cost,
  balance,
  featureLabel = "this AI feature",
  busy = false,
}) {
  const panelRef = useRef(null);
  // Guard cancellation while a debit is in flight.
  const safeCancel = busy ? () => {} : (onCancel || (() => {}));
  useDialogDismiss({ open, onClose: safeCancel, ref: panelRef });

  if (!open) return null;
  const sufficient = (balance ?? 0) >= (cost ?? 0);
  const shortBy = Math.max(0, (cost ?? 0) - (balance ?? 0));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Confirm AI usage"
        className="bg-bg-primary border border-border-default p-6 w-full max-w-sm rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onCancel}
          aria-label="Close"
          className="absolute top-3 right-3 text-text-secondary hover:text-text-primary"
          disabled={busy}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-button-primary/15 flex items-center justify-center">
            <Coins className="w-4 h-4 text-button-primary" />
          </div>
          <h3 className="text-base font-semibold text-text-primary">Confirm AI usage</h3>
        </div>

        <p className="text-sm text-text-secondary mb-4">
          Running <span className="text-text-primary">{featureLabel}</span> will deduct
          {" "}<span className="text-text-primary font-semibold">{cost}</span> Zencredits
          from your wallet.
        </p>

        <div className="bg-bg-secondary/40 rounded-md px-3 py-2 mb-5 text-xs flex items-center justify-between">
          <span className="text-text-secondary">Your balance</span>
          <span className={`font-semibold tabular-nums ${
            sufficient ? "text-emerald-300" : "text-rose-300"
          }`}>
            {balance ?? 0} Zencredits
          </span>
        </div>

        {!sufficient && (
          <div className="bg-rose-500/10 border border-rose-500/40 rounded-md px-3 py-2 mb-4 text-xs text-rose-200">
            Short by <span className="font-semibold">{shortBy}</span> Zencredits.
            Top up to continue.
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-sm text-text-secondary hover:bg-bg-secondary/40 disabled:opacity-50"
          >
            Cancel <span className="opacity-50 ml-1">(Esc)</span>
          </button>
          {sufficient ? (
            <button
              onClick={onConfirm}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm bg-button-primary text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Working…" : `Use ${cost} Zencredits`}
            </button>
          ) : (
            <Link
              to="/account/topup"
              onClick={onCancel}
              className="px-3 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:opacity-90 no-underline"
            >
              Buy Zencredits →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
